import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { DTypeRepository, DTypeStatus, NextLocator } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { catchError, firstValueFrom, map, Subject, takeUntil, throwError } from 'rxjs';
import { FormService } from '../form.service';
import { Form } from '../form.types';
import { UserNodePrivileges } from '../../../modules/admin/projects/projects.types';
import { DTypeAlerts, ToggleDTypeLockBody } from '../../dtype/dtype.types';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { DTypeService } from '../../dtype/dtype.service';
import { LocalizePipe } from '../../pipes/localize.pipe';
import { LocalizeService } from '../../pipes/localize.service';
import { ProjectsService } from '../../../modules/admin/projects/projects.service';
import { UserService } from '../../user/user.service';
import { DownloadMasterXSSFBody } from '../../master-data/master-data.types';
import { ConfirmationWithCountdownConfig } from '../../../dialogs/confirm-with-countdown-dialog/confirm-with-countdown-dialog.types';

@Component({
    selector: 'form-summary',
    templateUrl: './form-summary.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormSummaryComponent implements OnInit, OnDestroy {

    form: Form;

    params: Params;
    uri: string;
    locator: NextLocator;
    repo: DTypeRepository;
    role: UserNodePrivileges;
    sameLocator: boolean;

    showAlerts: DTypeAlerts = {
        showScheduleToGoLive: true,
        showMigrating: true,
        showHasRequestToGoLive: true,
    };
    scheduleAlert: string;

    databaseViewMode: boolean;
    quarantineViewMode: boolean;

    dtypeForm: DTypeForm;
    loading = true;

    private _unsubscribeAll = new Subject<void>();

    constructor(
        private activatedRoute: ActivatedRoute,
        private _changeDetectorRef: ChangeDetectorRef,
        private dialogsService: DialogsService,
        private dtypeService: DTypeService,
        private formService: FormService,
        private localize: LocalizePipe,
        private localizeService: LocalizeService,
        private projectsService: ProjectsService,
        private router: Router,
        private _snackbar: MatSnackBar,
        private translocoService: TranslocoService,
        private userService: UserService
    ) { }

    async ngOnInit() {
        this.activatedRoute.queryParams
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(async (params) => {
                this.loading = true;

                this.databaseViewMode = params.databaseViewMode ?? false;
                this.params = params;

                this.role = await this.projectsService.getRoleByLocators(params);

                if (params.uri && params.locator) {
                    this.uri = params.uri;
                    this.locator = this.formService.getLocatorFromString(this.uri.substring(7, this.uri.lastIndexOf('/'))); //form://
                    this.repo = cloneDeep(await firstValueFrom(this.formService.retrieveFormDTypeRepositoryByUri(this.uri, DTypeStatus.LIVE, this.locator)));
                    this.form = this.repo.payload as Form;
                    this.sameLocator = this.projectsService.isTheSameLocator(params.locator, this.repo.belongsTo);
                } else {
                    this.dialogsService.openErrorDialog();
                    this.router.navigate(['../'], { queryParams: { force: true } });
                    return;
                }

                this.dtypeForm = await this.formService.retrieveFormDtypeForm(this.form, { readonly: true, isUpdate: true });

                if (params?.dataVerifier && this.role?.dataVerifier && this.repo.locked && this.repo.dtypeToGoLiveRequest && !this.repo.dtypeToGoLiveSchedule) {
                    this.quarantineViewMode = true;
                }

                if (this.repo.dtypeToGoLiveSchedule && !this.repo.migrating) {
                    this.calculateScheduleAlert();
                }

                this._changeDetectorRef.detectChanges();
                this.loading = false;
            });
    }

    ngOnDestroy() {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    changeView() {
        this.databaseViewMode = !this.databaseViewMode;
    }

    async changeQuarantineView(reload: boolean = false) {
        if (reload) {
            this.loading = true;
            this.formService.retrieveFormDTypeRepositoryByUri(this.uri, DTypeStatus.LIVE, this.locator).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    console.error(error);
                    return throwError(() => error.error);
                })).subscribe(async (repo) => {
                this.repo = repo;
                this.form = this.repo.payload as Form;
                if (this.repo.dtypeToGoLiveSchedule && !this.repo.migrating) {
                    this.calculateScheduleAlert();
                }
                this.loading = false;
                this.quarantineViewMode = !this.quarantineViewMode;
            });
        } else {
            this.quarantineViewMode = !this.quarantineViewMode;
        }
    }

    hasPermissionToDesignRepository() {
        return this.projectsService.hasPermissionToDesignRepository(this.role, 'form');
    }

    deleteRepository() {
        this.dtypeService.deleteNextDType(this.uri).subscribe({
            next: (reply) => {
                if (reply?.status === 200) {
                    this._snackbar.open(this.translocoService.translate('deletedDType',
                        { name: this.localize.transform(this.form?.caption) }), this.translocoService.translate('accept'), { duration: 5000 });
                    this.goBack();
                } else {
                    this.dialogsService.openErrorDialog();
                }
            }, error: (err) => {
                console.error(err);
                this.dialogsService.openErrorDialog({
                    message: err?.error?.echo ? this.localize.transform(err?.error?.echo) : undefined,
                });
            }
        });
    }

    async downloadStructure() {
        const date = (format(new Date(), 'yyyy-MM-dd_HH-mm-ss'));
        const filenameFront = this.localize.transform(this.form?.name) + '_r_' + this.repo.release + '_d_' + date;
        const body: DownloadMasterXSSFBody = { uri: this.uri, language: this.translocoService.getActiveLang() };
        this.formService.downloadStructureXSSF(body).pipe(
            catchError((error) => {
                console.error(error);
                let _message = null;
                if (error?.status === 404) {
                    _message = 'No se ha encontrado el repositorio.';
                }
                this.dialogsService.openErrorDialog({
                    message: _message ? _message : undefined,
                });
                return throwError(() => error);
        })).subscribe((data) => {
            const filename = String(data?.headers?.get('content-disposition')).replace('attachment; filename="', '').replace('"', '');
            const element = document.createElement('a');
            element.href = window.URL.createObjectURL(data.body);
            element.download = filename ?? filenameFront;
            element.click();
        });
    }

    async toggleDTypeRepositoryLock() {
        this.loading = true;
        const config: ConfirmationWithCountdownConfig = {
            title: this.translocoService.translate(this.repo.locked ? 'blockRepoDialog.titleLocked' : 'blockRepoDialog.titleUnlocked'),
            message: this.translocoService.translate(this.repo.locked ? 'blockRepoDialog.messageLocked' : 'blockRepoDialog.messageUnlocked'),
            countdownMessage: this.translocoService.translate('blockRepoDialog.countdownMessage'),
            seconds: 10,
            actions: {
                confirm: {
                    show: true,
                    label: this.translocoService.translate(this.repo.locked ? 'blockRepoDialog.unlock' : 'blockRepoDialog.lock'),
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: this.translocoService.translate('cancel'),
                }
            }
        };

        const confirmed = await firstValueFrom(this.dialogsService.openConfirmWithCountdownDialog(config).afterClosed());

        if (confirmed === 'confirmed') {
            const body: ToggleDTypeLockBody = {
                uri: this.repo.uri
            };

            this.dtypeService.toggleDTypeLock(body).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.repository)
            ).subscribe(async (repo) => {
                this.repo = repo;
                this.form = repo.payload as Form;
                this.loading = false;
                this._snackbar.open(this.translocoService.translate(
                    this.repo.locked ? 'blockRepoDialog.lockedRepo' : 'blockRepoDialog.unlockedRepo'), this.translocoService.translate('accept'), { duration: 5000 });
            });
        }
        this.loading = false;
        //TODO AD: falta la parte de mostrar dependencias que está pendiente de servidor
    }

    async toggleDTypeRepositoryHidden() {
        this.loading = true;
        const config: ConfirmationWithCountdownConfig = {
            title: this.translocoService.translate(this.repo.hidden ? 'hideRepoDialog.show' : 'hideRepoDialog.hide'),
            message: this.translocoService.translate(this.repo.hidden ? 'hideRepoDialog.messageShow' : 'hideRepoDialog.messageHide'),
            countdownMessage: this.translocoService.translate('hideRepoDialog.countdownMessage'),
            seconds: 10,
            actions: {
                confirm: {
                    show: true,
                    label: this.translocoService.translate(this.repo.hidden ? 'hideRepoDialog.show' : 'hideRepoDialog.hide'),
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: this.translocoService.translate('cancel'),
                }
            }
        };

        const confirmed = await firstValueFrom(this.dialogsService.openConfirmWithCountdownDialog(config).afterClosed());

        if (confirmed === 'confirmed') {
            const body: ToggleDTypeLockBody = {
                uri: this.repo.uri
            };

            this.dtypeService.toggleDTypeHidden(body).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.repository)
            ).subscribe(async (repo) => {
                this.repo = repo;
                this.form = repo.payload as Form;
                this.loading = false;
                this._snackbar.open(this.translocoService.translate(
                    this.repo.hidden ? 'hideRepoDialog.hiddenRepo' : 'hideRepoDialog.showedRepo'), this.translocoService.translate('accept'), { duration: 5000 });
            });
        }
        this.loading = false;
    }

    async cancelQuarantine() {
        const confirm2 = await this.openConfirmDialogWithCountdown(
            this.translocoService.translate('quarantineReject.title'),
            this.translocoService.translate('quarantineReject.message'),
            this.translocoService.translate('quarantineReject.countdownMessage'));
        if (confirm2) {
            this.dtypeService.flushDTypeToGoLiveSchedule({uri: this.uri, rejected: true}).pipe(
                catchError((error) => {
                    this.dialogsService.openErrorDialog({
                        message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined,
                    });
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.repository)
            ).subscribe(async (repo) => {
                this.repo = repo;
                this.form = repo.payload as Form;
            });
        }
    }

    private goBack() {
        this.router.navigate(['../'], {
            queryParams: {
                ou: '01',
                marca_code: this.locator.marca_code,
                cliente_code: this.locator.cliente_code,
                projectId: this.locator.projectId,
                serviceId: this.locator.serviceId
            },
            relativeTo: this.activatedRoute
        });
    }

    private calculateScheduleAlert() {
        this.scheduleAlert = this.translocoService.translate('quarantineScheduled.part1') +
                    this.localizeService.transformDate(this.repo.dtypeToGoLiveSchedule.date, 'dd/MM/yyyy H:mm') +
                    (this.role?.dataVerifier ? this.translocoService.translate('quarantineScheduled.part2') : this.translocoService.translate('quarantineScheduled.part3'));
    }

    private openConfirmDialogWithCountdown(title: string, message: string, countdownMessage: string) {
        const dialog = this.dialogsService.openConfirmWithCountdownDialog({
            title,
            message,
            countdownMessage,
            seconds: 10,
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('revert'),
                }
            },
        });

        return new Promise<boolean>((resolve) => {
            dialog.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }

}
